import { useEffect, useState, Fragment } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT, CONCILIACION_MANUAL_CFDI_TITLE } from "@/modules/workModuleCore";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, detailFromAxiosError, fmtNumber } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, ChevronRight, ChevronDown, Trash2, ExternalLink, Monitor, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import ConciliacionManualExcelPanel from "@/components/ConciliacionManualExcelPanel";

async function downloadReportBlob(path, params, fallbackFilename) {
  try {
    const { data, headers } = await api.get(path, {
      responseType: "blob",
      params: params ?? undefined,
    });
    if (!(data instanceof Blob) || data.size === 0) {
      toast.error("El servidor devolvió un archivo vacío. Revisa la sesión o la pestaña Red (F12).");
      return;
    }
    const head = await data.slice(0, 8).arrayBuffer();
    const u8 = new Uint8Array(head);
    const looksZip = u8.length >= 4 && u8[0] === 0x50 && u8[1] === 0x4b; // .xlsx / .docx
    const startTxt = new TextDecoder("utf-8", { fatal: false }).decode(u8.slice(0, 4));
    const probablyJson = startTxt.trimStart().startsWith("{") || startTxt.trimStart().startsWith("[");
    if (probablyJson && !looksZip) {
      const text = await data.text();
      try {
        const j = JSON.parse(text);
        toast.error(formatApiError(j.detail) || text.slice(0, 400) || "Respuesta inválida al descargar");
      } catch {
        toast.error(text.slice(0, 400) || "Respuesta inválida al descargar");
      }
      return;
    }
    const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
    let name = fallbackFilename;
    const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    const quoted = cd.match(/filename="([^"]+)"/i);
    if (star) {
      try {
        name = decodeURIComponent(star[1]);
      } catch {
        name = star[1];
      }
    } else if (quoted) {
      name = quoted[1];
    }
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Descarga: ${name}`);
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (e) {
    const msg = await detailFromAxiosError(e);
    toast.error(msg || formatApiError(e.response?.data?.detail) || "No se pudo descargar el archivo");
  }
}


const fmtSyncAtLocal = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

export default function ConciliacionManualCFDI({
  embedMode = false,
  clientId: controlledClientId,
  setClientId: controlledSetClientId,
  year: controlledYear,
  setYear: controlledSetYear,
  clients: controlledClients,
} = {}) {
  const isEmbedded = embedMode === "carga";
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [openSections, setOpenSections] = useState([]);
  const [clientsInternal, setClientsInternal] = useState([]);
  const [clientIdInternal, setClientIdInternal] = usePersistedClientId(clientsInternal);
  const [yearInternal, setYearInternal] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const clients = controlledClients ?? clientsInternal;
  const clientId = controlledClientId ?? clientIdInternal;
  const setClientId = controlledSetClientId ?? setClientIdInternal;
  const year = controlledYear ?? yearInternal;
  const setYear = controlledSetYear ?? setYearInternal;
  const [cfdiStats, setCfdiStats] = useState(null);
  const [excelStats, setExcelStats] = useState(null);

  const [complementFlujo, setComplementFlujo] = useState("ingreso");
  /** emision | mes | rango | dia — filtro por fecha de emisión del CFDI */
  const [complementPeriodo, setComplementPeriodo] = useState("ejercicio");
  const [complementAnioMes, setComplementAnioMes] = useState(() => new Date().getFullYear());
  const [complementMesNum, setComplementMesNum] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [complementRangoDesde, setComplementRangoDesde] = useState("");
  const [complementRangoHasta, setComplementRangoHasta] = useState("");
  const [complementDia, setComplementDia] = useState("");
  const [complementMetodo, setComplementMetodo] = useState("");
  const [complementQ, setComplementQ] = useState("");
  const [complementItems, setComplementItems] = useState([]);
  const [complementSel, setComplementSel] = useState(() => new Set());
  const [complementOpts, setComplementOpts] = useState({});
  const [complementOpen, setComplementOpen] = useState(() => new Set());
  const [complementLoadBusy, setComplementLoadBusy] = useState(false);
  const [complementSaveBusy, setComplementSaveBusy] = useState(false);
  const [complementModalUuid, setComplementModalUuid] = useState(null);
  const [complementModalDraft, setComplementModalDraft] = useState(null);
  /** Mes/año del cuadre por **fecha de cobro o pago** (independiente del filtro por emisión). */
  const [complementCuadreAnio, setComplementCuadreAnio] = useState(() => new Date().getFullYear());
  const [complementCuadreMes, setComplementCuadreMes] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [complementDeclInBanco, setComplementDeclInBanco] = useState("");
  const [complementDeclInEfectivo, setComplementDeclInEfectivo] = useState("");
  const [complementDeclEgBanco, setComplementDeclEgBanco] = useState("");
  const [complementDeclEgEfectivo, setComplementDeclEgEfectivo] = useState("");
  const [complementCuadreData, setComplementCuadreData] = useState(null);
  const [complementCuadreBusy, setComplementCuadreBusy] = useState(false);
  const [complementDeclSaveBusy, setComplementDeclSaveBusy] = useState(false);
  const [syncLocalMeta, setSyncLocalMeta] = useState(null);
  const [conflictDetail, setConflictDetail] = useState(null);
  const [conflictDiagBusy, setConflictDiagBusy] = useState(false);
  const [auditRows, setAuditRows] = useState([]);

  /** Sugerencia: última fecha de pago en XML tipo P, si no emisión del CFDI. */
  const sugerirFechaCobroPago = (it) => {
    const pagos = it?.pagos_sat || [];
    const lastFp = pagos.length ? String(pagos[pagos.length - 1].fecha_pago || "").slice(0, 10) : "";
    return lastFp || String(it?.fecha || "").slice(0, 10);
  };

  const openComplementModal = (uuid, presetTipo) => {
    const it = complementItems.find((i) => i.uuid === uuid);
    if (!it || !complementSel.has(uuid) || it.conflicto_excel) return;
    const emi = String(it.fecha || "").slice(0, 10);
    const cur = complementOpts[uuid] || {};
    let tipo = cur.tipo === "parcial" ? "parcial" : "completo";
    if (presetTipo === "completo" || presetTipo === "parcial") tipo = presetTipo;
    let pagos = [];
    const canalCur = cur.canal === "efectivo" ? "efectivo" : "banco";
    if (tipo === "parcial") {
      pagos = Array.isArray(cur.pagosParciales)
        ? cur.pagosParciales.map((p) => ({
            fecha: String(p.fecha || "").slice(0, 10),
            importe: String(p.importe ?? ""),
            canal: p.canal === "efectivo" ? "efectivo" : "banco",
          }))
        : [];
      if (!pagos.length && String(cur.importe || "").trim()) {
        pagos = [
          {
            fecha: String(cur.fechaCobroPago || "").slice(0, 10) || sugerirFechaCobroPago(it),
            importe: String(cur.importe),
            canal: canalCur,
          },
        ];
      }
      if (!pagos.length) {
        pagos = [{ fecha: emi, importe: "", canal: "banco" }];
      }
    }
    let fechaCobro = String(cur.fechaCobroPago || "").trim().slice(0, 10);
    if (tipo === "completo") {
      if (!fechaCobro) fechaCobro = emi;
    } else {
      fechaCobro = "";
    }
    setComplementModalUuid(uuid);
    setComplementModalDraft({ tipo, fechaCobroPago: fechaCobro, pagosParciales: pagos, canal: canalCur });
  };

  const applyComplementModal = () => {
    if (!complementModalUuid || !complementModalDraft) return;
    const u = complementModalUuid;
    const d = complementModalDraft;
    const row = complementItems.find((i) => i.uuid === u);
    const tot = Number(row?.total) || 0;

    if (d.tipo === "parcial") {
      const pagosPayload = (d.pagosParciales || [])
        .map((p) => ({
          fecha: String(p.fecha || "").trim().slice(0, 10),
          importe: parseFloat(String(p.importe || "").replace(/,/g, "").trim()),
          canal: p.canal === "efectivo" ? "efectivo" : "banco",
        }))
        .filter((p) => p.fecha && Number.isFinite(p.importe) && p.importe > 0);
      if (!pagosPayload.length) {
        toast.error("Captura al menos un abono con fecha e importe mayores a cero.");
        return;
      }
      for (const p of pagosPayload) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(p.fecha)) {
          toast.error("Revisa que todas las fechas de abono estén completas (AAAA-MM-DD).");
          return;
        }
      }
      const sum = pagosPayload.reduce((a, p) => a + p.importe, 0);
      if (tot > 0 && sum > tot + 0.05) {
        toast.error("La suma de abonos no puede superar el total del CFDI.");
        return;
      }
    }

    setComplementOpts((p) => ({
      ...p,
      [u]: {
        tipo: d.tipo,
        canal: d.tipo === "completo" ? (d.canal === "efectivo" ? "efectivo" : "banco") : "banco",
        fechaCobroPago: d.tipo === "completo" ? d.fechaCobroPago : "",
        importe:
          d.tipo === "parcial" && d.pagosParciales.length === 1
            ? String(d.pagosParciales[0].importe || "").trim()
            : "",
        pagosParciales: d.tipo === "parcial" ? d.pagosParciales : [],
      },
    }));
    setComplementModalUuid(null);
    setComplementModalDraft(null);
  };

  const complementModalItem = complementModalUuid ? complementItems.find((i) => i.uuid === complementModalUuid) : null;

  const loadStats = async () => {
    if (!clientId) return;
    try {
      const { data: cfdi } = await api.get("/cfdi/summary", { params: { client_id: clientId, year } });
      setCfdiStats(cfdi);
    } catch { setCfdiStats(null); }
    try {
      const { data: chk } = await api.get("/excel/check-existing", { params: { client_id: clientId, year } });
      setExcelStats(chk);
    } catch { setExcelStats(null); }
  };

  const loadDiagnostics = async () => {
    if (!clientId) {
      setSyncLocalMeta(null);
      setConflictDetail(null);
      setAuditRows([]);
      return;
    }
    setConflictDiagBusy(true);
    try {
      const [{ data: sync }, { data: cx }, { data: au }] = await Promise.all([
        api.get("/cfdi/client-sync-meta", { params: { client_id: clientId } }),
        api.get("/excel/modo-complemento/conflictos-detalle", {
          params: { client_id: clientId, year, flujo: complementFlujo },
        }),
        api.get("/excel/modo-complemento/bitacora", { params: { client_id: clientId, year, limit: 25 } }),
      ]);
      setSyncLocalMeta(sync);
      setConflictDetail(cx);
      setAuditRows(au?.items || []);
    } catch {
      setSyncLocalMeta(null);
      setConflictDetail(null);
      setAuditRows([]);
    } finally {
      setConflictDiagBusy(false);
    }
  };

  const openConflictInVisor = async (uuid) => {
    const u = (uuid || "").trim();
    if (!u) return;
    try {
      await goToSatVisorForUuid(navigate, clientId, u);
    } catch {
      toast.error("No se pudo abrir el visor para este UUID.");
    }
  };

  const loadModoComplemento = async (withSearch = false) => {
    if (!clientId) return;
    setComplementLoadBusy(true);
    try {
      const params = { client_id: clientId, year, flujo: complementFlujo };
      if (complementPeriodo === "mes") {
        params.mes = `${complementAnioMes}-${complementMesNum}`;
      } else if (complementPeriodo === "rango") {
        if (complementRangoDesde) params.fecha_desde = complementRangoDesde.slice(0, 10);
        if (complementRangoHasta) params.fecha_hasta = complementRangoHasta.slice(0, 10);
      } else if (complementPeriodo === "dia" && complementDia) {
        const d = complementDia.slice(0, 10);
        params.fecha_desde = d;
        params.fecha_hasta = d;
      }
      if (complementMetodo) params.metodo = complementMetodo;
      if (withSearch && complementQ.trim()) params.q = complementQ.trim();
      const { data } = await api.get("/excel/modo-complemento/candidatos", { params });
      const items = data.items || [];
      setComplementItems(items);
      const sel = new Set(items.filter((i) => i.marcado_modo && !i.conflicto_excel).map((i) => i.uuid));
      setComplementSel(sel);
      const nextOpts = {};
      for (const it of items) {
        if (it.modo_guardado && it.modo_guardado.tipo) {
          const tipoG = it.modo_guardado.tipo === "parcial" ? "parcial" : "completo";
          const pp = it.modo_guardado.pagos_parciales;
          const canalG =
            tipoG === "completo" && it.modo_guardado.canal === "efectivo" ? "efectivo" : "banco";
          const pagos =
            Array.isArray(pp) && pp.length
              ? pp.map((p) => ({
                  fecha: String(p.fecha || "").slice(0, 10),
                  importe: String(p.importe != null ? p.importe : ""),
                  canal: p.canal === "efectivo" ? "efectivo" : "banco",
                }))
              : [];
          nextOpts[it.uuid] = {
            tipo: tipoG,
            canal: canalG,
            importe:
              tipoG === "parcial" && !pagos.length && it.modo_guardado.importe != null
                ? String(it.modo_guardado.importe)
                : "",
            fechaCobroPago:
              String(it.modo_guardado.fecha_cobro_pago || "").slice(0, 10) || sugerirFechaCobroPago(it),
            pagosParciales: pagos,
          };
        }
      }
      for (const u of sel) {
        if (!nextOpts[u]) {
          const row = items.find((x) => x.uuid === u);
          nextOpts[u] = { tipo: "completo", canal: "banco", importe: "", fechaCobroPago: sugerirFechaCobroPago(row), pagosParciales: [] };
        }
      }
      setComplementOpts(nextOpts);
      setComplementOpen(new Set());
      setComplementModalUuid(null);
      setComplementModalDraft(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar la lista");
    } finally {
      setComplementLoadBusy(false);
    }
  };

  const saveModoComplemento = async () => {
    if (!clientId) {
      toast.error("Selecciona cliente");
      return;
    }
    const itemsPayload = [];
    for (const u of complementSel) {
      const opt = complementOpts[u] || {
        tipo: "completo",
        canal: "banco",
        importe: "",
        fechaCobroPago: "",
        pagosParciales: [],
      };
      const row = complementItems.find((i) => i.uuid === u);
      const tipo = opt.tipo === "parcial" ? "parcial" : "completo";

      if (tipo === "parcial") {
        const rawLines = Array.isArray(opt.pagosParciales) ? opt.pagosParciales : [];
        let pagosPayload = rawLines
          .map((p) => ({
            fecha: String(p.fecha || "").trim().slice(0, 10),
            importe: parseFloat(String(p.importe || "").replace(/,/g, "").trim()),
            canal: p.canal === "efectivo" ? "efectivo" : "banco",
          }))
          .filter((p) => p.fecha && Number.isFinite(p.importe) && p.importe > 0);
        if (pagosPayload.length === 0) {
          const raw = String(opt.importe || "").replace(/,/g, "").trim();
          const v = parseFloat(raw);
          let fc = String(opt.fechaCobroPago || "").trim().slice(0, 10);
          if (!Number.isFinite(v) || v <= 0) {
            toast.error(`Parcial sin abonos válidos (${u.slice(0, 8)}…). Abre la ventana de captura.`);
            return;
          }
          if (!fc && row) fc = sugerirFechaCobroPago(row);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(fc)) {
            toast.error(`Fecha de abono inválida (${u.slice(0, 8)}…).`);
            return;
          }
          const canalAbono = opt.canal === "efectivo" ? "efectivo" : "banco";
          pagosPayload = [{ fecha: fc, importe: v, canal: canalAbono }];
        }
        const sum = pagosPayload.reduce((a, p) => a + p.importe, 0);
        if (row && sum > (Number(row.total) || 0) + 0.05) {
          toast.error(`La suma de abonos supera el total del CFDI (${u.slice(0, 8)}…).`);
          return;
        }
        for (const p of pagosPayload) {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(p.fecha)) {
            toast.error(`Fecha inválida en abonos (${u.slice(0, 8)}…).`);
            return;
          }
        }
        const lastF = pagosPayload[pagosPayload.length - 1].fecha;
        itemsPayload.push({
          uuid: u,
          tipo: "parcial",
          pagos_parciales: pagosPayload,
          fecha_cobro_pago: lastF,
          canal: opt.canal === "efectivo" ? "efectivo" : "banco",
        });
        continue;
      }

      let fc = String(opt.fechaCobroPago || "").trim().slice(0, 10);
      if (!fc && row) fc = sugerirFechaCobroPago(row);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fc)) {
        toast.error(`Fecha de cobro/pago inválida (${u.slice(0, 8)}…). Usa el calendario o formato AAAA-MM-DD.`);
        return;
      }
      const canalC = opt.canal === "efectivo" ? "efectivo" : "banco";
      itemsPayload.push({ uuid: u, tipo: "completo", fecha_cobro_pago: fc, canal: canalC });
    }
    setComplementSaveBusy(true);
    try {
      const { data } = await api.post("/excel/modo-complemento/guardar", {
        client_id: clientId,
        year,
        flujo: complementFlujo,
        items: itemsPayload,
      });
      const bc = (data.skipped_conflicto_excel || []).length;
      const bn = (data.skipped_not_found || []).length;
      const bi = (data.skipped_invalid || []).length;
      let msg = `Guardado: ${data.inserted} filas (${data.deleted_previous} anteriores sustituidas).`;
      if (bc) msg += ` Omitidos (ya en Excel/CSV): ${bc}.`;
      if (bn) msg += ` No encontrados: ${bn}.`;
      if (bi) msg += ` Parcial sin importe válido: ${bi}.`;
      toast.success(msg);
      loadStats();
      await loadModoComplemento(false);
      loadComplementCuadre();
      loadDiagnostics();
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      if (st === 409 && detail?.code === "EXCEL_MONTH_SOURCE_CONFLICT") {
        toast.error(
          (detail && detail.message) || formatApiError(detail)
            || "Ese mes ya tiene contabilidad por otro método; use «Limpiar mes» en Carga de información.",
          { duration: 18000 },
        );
      } else {
        toast.error(formatApiError(detail) || "No se pudo guardar");
      }
    } finally {
      setComplementSaveBusy(false);
    }
  };

  const loadComplementCuadre = async () => {
    if (!clientId) return;
    const ym = `${complementCuadreAnio}-${complementCuadreMes}`;
    setComplementCuadreBusy(true);
    try {
      const { data } = await api.get("/excel/modo-complemento/cuadre-mes", {
        params: { client_id: clientId, year, year_month: ym },
      });
      setComplementCuadreData(data);
      const dcl = data.declarado || {};
      setComplementDeclInBanco(dcl.ingreso_decl_banco != null ? String(dcl.ingreso_decl_banco) : "");
      setComplementDeclInEfectivo(dcl.ingreso_decl_efectivo != null ? String(dcl.ingreso_decl_efectivo) : "");
      setComplementDeclEgBanco(dcl.egreso_decl_banco != null ? String(dcl.egreso_decl_banco) : "");
      setComplementDeclEgEfectivo(dcl.egreso_decl_efectivo != null ? String(dcl.egreso_decl_efectivo) : "");
    } catch (e) {
      setComplementCuadreData(null);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar el cuadre del mes");
    } finally {
      setComplementCuadreBusy(false);
    }
  };

  const saveComplementDeclaracionMes = async () => {
    if (!clientId) return;
    const ym = `${complementCuadreAnio}-${complementCuadreMes}`;
    setComplementDeclSaveBusy(true);
    try {
      await api.post("/excel/modo-complemento/declaracion-mes", {
        client_id: clientId,
        year,
        year_month: ym,
        ingreso_decl_banco: parseFloat(String(complementDeclInBanco || "0").replace(/,/g, "")) || 0,
        ingreso_decl_efectivo: parseFloat(String(complementDeclInEfectivo || "0").replace(/,/g, "")) || 0,
        egreso_decl_banco: parseFloat(String(complementDeclEgBanco || "0").replace(/,/g, "")) || 0,
        egreso_decl_efectivo: parseFloat(String(complementDeclEgEfectivo || "0").replace(/,/g, "")) || 0,
      });
      toast.success("Declaración del mes guardada");
      await loadComplementCuadre();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setComplementDeclSaveBusy(false);
    }
  };

  const downloadComplementCuadreExcel = async () => {
    if (!clientId) return;
    const ym = `${complementCuadreAnio}-${complementCuadreMes}`;
    await downloadReportBlob(
      "/excel/modo-complemento/reporte-cuadre-xlsx",
      { client_id: clientId, year, year_month: ym },
      `cuadre_conciliacion_${ym}.xlsx`,
    );
  };

  const toggleComplementOpen = (uuid) => {
    setComplementOpen((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  useEffect(() => {
    if (isEmbedded && controlledClients) return;
    api.get("/clients").then(({ data }) => setClientsInternal(data));
  }, [isEmbedded, controlledClients]);

  useEffect(() => {
    if (!clientId || !year) return;
    localStorage.setItem("vf:selected_year", String(year));
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year]);

  useEffect(() => {
    setComplementAnioMes(year);
  }, [year]);

  useEffect(() => {
    loadDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year, complementFlujo]);

  useEffect(() => {
    if (!clientId) return;
    loadComplementCuadre();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year, complementCuadreAnio, complementCuadreMes]);

  useEffect(() => {
    if (!clientId) return;
    loadModoComplemento(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year, complementFlujo, complementMetodo, complementPeriodo, complementAnioMes, complementMesNum, complementRangoDesde, complementRangoHasta, complementDia]);

  const totalCfdi = (cfdiStats || []).reduce((s, r) => s + (r.count || 0), 0);

  useEffect(() => {
    if (isEmbedded) return undefined;
    setOpenSections([]);
    setSearchParams((prev) => {
      if (!prev.get("tab")) return prev;
      const next = new URLSearchParams(prev);
      next.delete("tab");
      return next;
    }, { replace: true });
    return () => {
      setOpenSections([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOpenSectionsChange = (values) => {
    setOpenSections(values);
    if (isEmbedded) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (values.length > 0) {
        next.set("tab", values[values.length - 1]);
      } else {
        next.delete("tab");
      }
      return next;
    }, { replace: true });
  };

  const accordionBlock = (
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={onOpenSectionsChange}
          className={isEmbedded ? "rounded-sm border border-slate-100 bg-slate-50/40" : "rounded-sm border border-slate-200 bg-white shadow-sm"}
        >
          <AccordionItem value="sistema" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-conciliacion-sistema">
              <span className="flex items-center gap-2">
                <Monitor className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Conciliación manual en pantalla (Sistema)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4 space-y-5">

        <div className="rounded-lg border border-amber-300 bg-amber-50/90 p-4 text-sm text-amber-950 space-y-2">
          <div className="flex items-start gap-2 font-semibold text-amber-900">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            Importante · una sola fuente para confrontar
          </div>
          <p className="text-[13px] leading-snug">
            Si vas a usar <strong>esta conciliación manual</strong> como control principal para confrontar ingresos o egresos
            (mismos UUID y mismo ejercicio), <strong>no cargues además</strong> los mismos datos por{" "}
            <strong>Excel Contalink / plantilla / CSV</strong>: genera duplicidad, bloqueos por UUID y resultados confusos en confronta.
            Lo recomendable es tener solo los <strong>XML del SAT</strong> cargados y trabajar aquí; si ya usas Excel/CSV para ese flujo,
            mantén ese criterio y evita marcar los mismos UUID en esta pantalla, o bien borra las filas importadas antes de usar la conciliación manual.
          </p>
          <p className="text-[12px]">
            Carga de archivos: <Link className="text-[#0F2D5C] underline font-medium" to="/carga-informacion">Excel / plantilla</Link>
            {" · "}
            <Link className="text-[#0F2D5C] underline font-medium" to="/carga-informacion">CSV</Link>
            {" · "}
            <Link className="text-[#0F2D5C] underline font-medium" to="/carga-informacion">XML</Link>
          </p>
        </div>

        <details className="group border border-slate-200 bg-white rounded-md text-[11px] text-slate-700">
          <summary className="cursor-pointer px-4 py-2 font-semibold text-slate-800 list-none flex items-center gap-2 [&::-webkit-details-marker]:hidden">
            <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-90" />
            Diagnóstico: doble fuente Excel/CSV y bitácora de guardados · ejercicio {year}
          </summary>
          <div className="px-4 pb-4 pt-2 border-t border-slate-100">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-md border border-slate-200 p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Doble fuente Excel/CSV · {complementFlujo === "ingreso" ? "Ingresos" : "Egresos"}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 text-[11px]"
                    disabled={conflictDiagBusy || !clientId}
                    onClick={() => loadDiagnostics()}
                  >
                    {conflictDiagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    <span className="ml-1">Actualizar</span>
                  </Button>
                </div>
                {syncLocalMeta && (
                  <p className="text-[11px] text-slate-600 leading-snug">
                    Última integración de XML en esta instalación:{" "}
                    <span className="font-mono">{fmtSyncAtLocal(syncLocalMeta.last_xml_ingested_at)}</span>
                    {" · "}
                    <span className="font-mono">{fmtNumber(syncLocalMeta.cfdi_count)}</span> CFDI.{" "}
                    <span className="text-slate-500">{syncLocalMeta.disclaimer}</span>
                  </p>
                )}
                {conflictDiagBusy ? (
                  <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando diagnóstico…
                  </div>
                ) : conflictDetail && conflictDetail.count > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-900 font-medium">
                      {fmtNumber(conflictDetail.count)} UUID bloqueados: ya tienen fila de Excel/CSV (u origen distinto a conciliación
                      manual) en el mismo ejercicio fiscal {year}. No se pueden guardar de nuevo desde esta pantalla hasta resolver duplicidad.
                    </p>
                    <div className="max-h-52 overflow-auto border border-slate-200 rounded-sm text-[11px]">
                      <table className="w-full">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold text-slate-600">UUID</th>
                            <th className="text-left px-2 py-1 font-semibold text-slate-600">Origen</th>
                            <th className="text-left px-2 py-1 font-semibold text-slate-600">Archivo</th>
                            <th className="text-left px-2 py-1 font-semibold text-slate-600">F. ingreso</th>
                            <th className="w-8" />
                          </tr>
                        </thead>
                        <tbody>
                          {(conflictDetail.filas_conflicto || []).slice(0, 100).map((row, i) => (
                            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/80">
                              <td className="px-2 py-1 font-mono text-[10px]">{row.uuid?.slice(0, 8)}…</td>
                              <td className="px-2 py-1">{row.excel_source || "—"}</td>
                              <td className="px-2 py-1 truncate max-w-[140px]" title={row.filename}>
                                {row.filename || "—"}
                              </td>
                              <td className="px-2 py-1 font-mono">{row.fecha_ingreso || "—"}</td>
                              <td className="px-1 py-1">
                                <button
                                  type="button"
                                  className="p-0.5 rounded-sm text-[#0F2D5C] hover:bg-slate-100"
                                  title="Abrir en visor SAT"
                                  onClick={() => openConflictInVisor(row.uuid)}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-900 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    Sin UUID bloqueados por doble fuente para este ejercicio y flujo.
                  </p>
                )}
              </div>

              <div className="rounded-md border border-slate-200 p-4 space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">Bitácora de guardados (quién / cuándo)</h3>
                {!auditRows.length ? (
                  <p className="text-[11px] text-slate-500">
                    Aún no hay operaciones registradas para el ejercicio {year}. Tras guardar conciliación, aparecerán aquí con muestra
                    de UUID y conteos.
                  </p>
                ) : (
                  <div className="max-h-56 overflow-auto space-y-2 text-[11px]">
                    {auditRows.map((a) => (
                      <div key={a.id} className="border border-slate-100 rounded-sm p-2.5 bg-slate-50/90">
                        <div className="font-mono text-slate-800">{String(a.created_at || "").slice(0, 19).replace("T", " ")}</div>
                        <div className="text-slate-600 mt-0.5">
                          <span className="font-medium">{a.actor_email || "—"}</span>
                          {a.actor_name ? ` (${a.actor_name})` : ""} · {a.flujo} · insertados {a.inserted ?? "—"} · borrados prev.{" "}
                          {a.deleted_previous ?? "—"}
                        </div>
                        {Array.isArray(a.uuid_muestra) && a.uuid_muestra.length > 0 && (
                          <div className="text-[10px] text-slate-500 mt-1 font-mono break-all">
                            UUID: {a.uuid_muestra.slice(0, 5).map((u) => u?.slice(0, 8)).join(", ")}
                            {a.uuid_muestra.length > 5 ? "…" : ""}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </details>

        <details className="group border border-slate-200 bg-white rounded-md text-[11px] text-slate-700">
          <summary className="cursor-pointer px-4 py-2 font-semibold text-slate-800 list-none flex items-center gap-2 [&::-webkit-details-marker]:hidden">
            <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-90" />
            Notas: pagos parciales, NC y canal banco/efectivo
          </summary>
          <div className="px-4 pb-3 pt-0 border-t border-slate-100 space-y-2 text-slate-600">
            <p>
              Los abonos parciales generan varias fechas de cobro o pago; el <strong>cuadre mensual</strong> agrupa por la fecha de
              cada abono y el canal (banco o efectivo) que indiques.
            </p>
            <p>
              Las <strong>notas de crédito</strong> pueden anular o ajustar importes de facturas relacionadas; si confrontas contra Excel,
              asegúrate de que ambos lados reflejen el mismo criterio (neto vs bruto, IVA acreditable, etc.).
            </p>
          </div>
        </details>

            <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
              <p className="text-xs text-slate-600">
                Marca facturas <strong>tipo I</strong> y define si el control es <strong>completo</strong> (todo el total del CFDI) o <strong>parcial</strong> (uno o varios abonos con fecha e importe hasta cubrir el saldo).
                Si hay <strong>complementos de pago</strong> cargados, se listan como referencia SAT. Requiere XML en el sistema. Si el UUID ya está en Excel/CSV, la fila queda bloqueada.
                <span className="block mt-1 text-slate-500">En la captura indica si cada cobro/pago fue por <strong>banco</strong> o <strong>efectivo</strong> para cuadrar con auxiliares. Orden de columnas: factura, emisión, total; <strong>Pago completo</strong> y <strong>Parcialidades</strong> abren la ventana de captura.</span>
              </p>
              {!clientId && (
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Selecciona un <strong>cliente</strong> en la barra superior para cargar la lista.
                </p>
              )}
              {clientId && (
                <div className="rounded-lg border border-slate-300 bg-gradient-to-br from-slate-50 to-white shadow-sm p-4 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-[#0F2D5C]">Control del mes · fecha de cobro o pago</h3>
                      <p className="text-[11px] text-slate-600 mt-1 max-w-2xl">
                        Compara lo que declaras en <strong>bancos</strong> y <strong>efectivo</strong> contra lo que quedó conciliado aquí (según la fecha de cobro/pago de cada CFDI y el canal que marques al capturar). El ejercicio de pantalla es <strong>{year}</strong> (filas de conciliación manual guardadas bajo ese año).
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8 text-xs"
                        disabled={complementCuadreBusy}
                        onClick={() => loadComplementCuadre()}
                      >
                        {complementCuadreBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        <span className="ml-1">Actualizar cuadre</span>
                      </Button>
                      <Button
                        type="button"
                        className="h-8 text-xs bg-[#0F2D5C] hover:bg-[#0b2249] text-white"
                        disabled={!clientId}
                        onClick={() => downloadComplementCuadreExcel()}
                      >
                        Excel cuadre
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Año calendario (cobro/pago)</Label>
                      <Select value={String(complementCuadreAnio)} onValueChange={(v) => setComplementCuadreAnio(Number(v))}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 18 }, (_, i) => year - 12 + i).map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Mes (cobro/pago)</Label>
                      <Select value={complementCuadreMes} onValueChange={setComplementCuadreMes}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((nombre, i) => {
                            const m = String(i + 1).padStart(2, "0");
                            return <SelectItem key={m} value={m}>{nombre}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-700">Ingresos del mes — declarado (auxiliar)</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-slate-500">Bancos</Label>
                          <Input
                            className="mt-0.5 h-9 text-xs font-mono text-right"
                            inputMode="decimal"
                            value={complementDeclInBanco}
                            onChange={(e) => setComplementDeclInBanco(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Efectivo</Label>
                          <Input
                            className="mt-0.5 h-9 text-xs font-mono text-right"
                            inputMode="decimal"
                            value={complementDeclInEfectivo}
                            onChange={(e) => setComplementDeclInEfectivo(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-700">Egresos del mes — declarado (auxiliar)</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-slate-500">Bancos</Label>
                          <Input
                            className="mt-0.5 h-9 text-xs font-mono text-right"
                            inputMode="decimal"
                            value={complementDeclEgBanco}
                            onChange={(e) => setComplementDeclEgBanco(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Efectivo</Label>
                          <Input
                            className="mt-0.5 h-9 text-xs font-mono text-right"
                            inputMode="decimal"
                            value={complementDeclEgEfectivo}
                            onChange={(e) => setComplementDeclEgEfectivo(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-9 text-xs"
                      disabled={complementDeclSaveBusy || !clientId}
                      onClick={() => saveComplementDeclaracionMes()}
                    >
                      {complementDeclSaveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      <span className={complementDeclSaveBusy ? "ml-1" : ""}>Guardar declaración del mes</span>
                    </Button>
                    <span className="text-[10px] text-slate-500 self-center">
                      Mes de cuadre: <span className="font-mono">{complementCuadreAnio}-{complementCuadreMes}</span>
                    </span>
                  </div>
                  {complementCuadreData && (
                    <div className="overflow-x-auto rounded-md border border-slate-200">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-slate-600">Concepto</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Banco decl.</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Efe. decl.</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Banco sist.</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Efe. sist.</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Δ Banco</th>
                            <th className="text-right px-2 py-2 font-semibold text-slate-600">Δ Efe.</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-600">Δ Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-slate-100 bg-white">
                            <td className="px-3 py-2 font-medium text-slate-800">Ingresos</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.declarado?.ingreso_decl_banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.declarado?.ingreso_decl_efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono text-emerald-800">{Number(complementCuadreData.conciliado_fecha_cobro_pago?.ingreso?.banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono text-emerald-800">{Number(complementCuadreData.conciliado_fecha_cobro_pago?.ingreso?.efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.diferencias?.ingreso?.banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.diferencias?.ingreso?.efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-3 py-2 font-mono font-semibold">{Number(complementCuadreData.diferencias?.ingreso?.total ?? 0).toFixed(2)}</td>
                          </tr>
                          <tr className="border-t border-slate-100 bg-slate-50/80">
                            <td className="px-3 py-2 font-medium text-slate-800">Egresos</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.declarado?.egreso_decl_banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.declarado?.egreso_decl_efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono text-emerald-800">{Number(complementCuadreData.conciliado_fecha_cobro_pago?.egreso?.banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono text-emerald-800">{Number(complementCuadreData.conciliado_fecha_cobro_pago?.egreso?.efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.diferencias?.egreso?.banco ?? 0).toFixed(2)}</td>
                            <td className="text-right px-2 py-2 font-mono">{Number(complementCuadreData.diferencias?.egreso?.efectivo ?? 0).toFixed(2)}</td>
                            <td className="text-right px-3 py-2 font-mono font-semibold">{Number(complementCuadreData.diferencias?.egreso?.total ?? 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Flujo</Label>
                  <Select value={complementFlujo} onValueChange={setComplementFlujo}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ingreso">Ingresos (CFDI emitidos · cobrado)</SelectItem>
                      <SelectItem value="egreso">Egresos (CFDI recibidos · pagado)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Periodo · emisión CFDI</Label>
                  <Select value={complementPeriodo} onValueChange={setComplementPeriodo}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ejercicio">Todo el ejercicio ({year})</SelectItem>
                      <SelectItem value="mes">Mes y año (cualquier año)</SelectItem>
                      <SelectItem value="rango">Rango de fechas</SelectItem>
                      <SelectItem value="dia">Un día</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {complementPeriodo === "mes" && (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Año</Label>
                      <Select value={String(complementAnioMes)} onValueChange={(v) => setComplementAnioMes(Number(v))}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 18 }, (_, i) => year - 12 + i).map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Mes</Label>
                      <Select value={complementMesNum} onValueChange={setComplementMesNum}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((nombre, i) => {
                            const m = String(i + 1).padStart(2, "0");
                            return <SelectItem key={m} value={m}>{nombre}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {complementPeriodo === "rango" && (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Desde</Label>
                      <Input type="date" className="mt-1 h-9 text-xs" value={complementRangoDesde} onChange={(e) => setComplementRangoDesde(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Hasta</Label>
                      <Input type="date" className="mt-1 h-9 text-xs" value={complementRangoHasta} onChange={(e) => setComplementRangoHasta(e.target.value)} />
                    </div>
                  </>
                )}
                {complementPeriodo === "dia" && (
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Día de emisión</Label>
                    <Input type="date" className="mt-1 h-9 text-xs" value={complementDia} onChange={(e) => setComplementDia(e.target.value)} />
                  </div>
                )}
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Método</Label>
                  <Select value={complementMetodo || "__all__"} onValueChange={(v) => setComplementMetodo(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      <SelectItem value="PUE">PUE</SelectItem>
                      <SelectItem value="PPD">PPD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-1 max-w-xl">
                <Label className="text-[10px] uppercase text-slate-500">Buscar</Label>
                <div className="flex gap-2">
                  <Input
                    className="h-9 text-xs"
                    placeholder="UUID, RFC, folio…"
                    value={complementQ}
                    onChange={(e) => setComplementQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadModoComplemento(true)}
                  />
                  <Button type="button" variant="outline" className="h-9 shrink-0 text-xs" onClick={() => loadModoComplemento(true)}>
                    Buscar
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button type="button" variant="outline" className="h-8 text-xs" onClick={() => loadModoComplemento(false)} disabled={complementLoadBusy || !clientId}>
                  {complementLoadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  <span className="ml-1">Actualizar lista</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => {
                    const nextSel = new Set(complementItems.filter((i) => !i.conflicto_excel).map((i) => i.uuid));
                    setComplementSel(nextSel);
                    setComplementOpts((p) => {
                      const n = { ...p };
                      for (const it of complementItems) {
                        if (it.conflicto_excel) continue;
                        n[it.uuid] = n[it.uuid] || { tipo: "completo", canal: "banco", importe: "", fechaCobroPago: sugerirFechaCobroPago(it), pagosParciales: [] };
                      }
                      return n;
                    });
                  }}
                  disabled={!complementItems.length}
                >
                  Seleccionar visibles
                </Button>
                <Button type="button" variant="outline" className="h-8 text-xs" onClick={() => setComplementSel(new Set())}>
                  Quitar selección
                </Button>
                <span className="text-[11px] text-slate-500">
                  {complementItems.length} CFDI · {complementSel.size} marcados
                </span>
              </div>
              <div className="max-h-[520px] overflow-auto border border-slate-200 rounded-md">
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr>
                      <th className="w-7 px-0 py-2" />
                      <th className="w-8 px-1 py-2 text-left" />
                      <th className="text-left px-2 py-2 font-semibold text-slate-600">Factura</th>
                      <th className="text-left px-2 py-2 font-semibold text-slate-600 whitespace-nowrap">Emisión</th>
                      <th className="text-right px-2 py-2 font-semibold text-slate-600">Monto total</th>
                      <th className="text-center px-1 py-2 font-semibold text-slate-600 whitespace-nowrap">Moneda</th>
                      <th className="text-right px-1 py-2 font-semibold text-slate-600 whitespace-nowrap">T.C.</th>
                      <th className="text-right px-2 py-2 font-semibold text-slate-600 whitespace-nowrap">Cobrado/pagado MXN</th>
                      <th className="text-center px-1 py-2 font-semibold text-slate-600 whitespace-nowrap">Pago completo</th>
                      <th className="text-center px-1 py-2 font-semibold text-slate-600 whitespace-nowrap">Parcialidades</th>
                      <th className="text-left px-2 py-2 font-semibold text-slate-600 min-w-[118px]">Resumen</th>
                      <th className="text-left px-2 py-2 font-semibold text-slate-600">Contraparte</th>
                      <th className="text-left px-2 py-2 font-semibold text-slate-600">Método</th>
                      <th className="text-left px-2 py-2 font-semibold text-slate-600">UUID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complementItems.length === 0 && (
                      <tr>
                        <td colSpan={14} className="px-3 py-8 text-center text-slate-500">
                          {complementLoadBusy ? "Cargando…" : "No hay CFDI tipo I en el periodo. Carga XML del SAT o ajusta filtros."}
                        </td>
                      </tr>
                    )}
                    {complementItems.map((it) => {
                      const opt = complementOpts[it.uuid] || {
                        tipo: "completo",
                        canal: "banco",
                        importe: "",
                        fechaCobroPago: "",
                        pagosParciales: [],
                      };
                      const sugerida = sugerirFechaCobroPago(it);
                      const open = complementOpen.has(it.uuid);
                      const facturaLbl = [it.serie, it.folio].filter(Boolean).join("-") || "—";
                      let resumenTxt = "—";
                      if (complementSel.has(it.uuid) && !it.conflicto_excel) {
                        if (opt.tipo !== "parcial") {
                          const f = String(opt.fechaCobroPago || "").trim().slice(0, 10) || it.fecha || "—";
                          const ch = opt.canal === "efectivo" ? "Efectivo" : "Banco";
                          resumenTxt = `Completo · ${f} · ${ch}`;
                        } else {
                          const lines = opt.pagosParciales || [];
                          const sum = lines.reduce((a, p) => a + (parseFloat(String(p.importe || "").replace(/,/g, "")) || 0), 0);
                          const saldo = Math.max((Number(it.total) || 0) - sum, 0);
                          const n = lines.filter((p) => {
                            const v = parseFloat(String(p.importe || "").replace(/,/g, ""));
                            return Number.isFinite(v) && v > 0;
                          }).length;
                          const bco = lines.filter((p) => {
                            const v = parseFloat(String(p.importe || "").replace(/,/g, ""));
                            return Number.isFinite(v) && v > 0 && p.canal !== "efectivo";
                          }).length;
                          const efe = lines.filter((p) => {
                            const v = parseFloat(String(p.importe || "").replace(/,/g, ""));
                            return Number.isFinite(v) && v > 0 && p.canal === "efectivo";
                          }).length;
                          resumenTxt = n
                            ? `Parcial · ${n} abono(s) · bco ${bco}/efe ${efe} · saldo ${saldo.toFixed(2)}`
                            : "Parcial · abre captura";
                        }
                      } else if (it.modo_guardado?.tipo) {
                        const mg = it.modo_guardado;
                        if (mg.tipo === "completo") {
                          const ch = mg.canal === "efectivo" ? "Efectivo" : "Banco";
                          resumenTxt = `Completo · ${(mg.fecha_cobro_pago || "").slice(0, 10) || "—"} · ${ch}`;
                        } else {
                          const n = (mg.pagos_parciales || []).length;
                          if (n) resumenTxt = `Parcial · ${n} abono(s) · saldo ${Number(mg.saldo || 0).toFixed(2)}`;
                          else resumenTxt = `Parcial · ${Number(mg.importe || 0).toFixed(2)}`;
                        }
                      }
                      return (
                        <Fragment key={it.uuid}>
                          <tr className={`border-t border-slate-100 ${it.conflicto_excel ? "bg-amber-50/80" : ""}`}>
                            <td className="px-0 py-0 align-middle text-center">
                              <button
                                type="button"
                                className="p-1 text-slate-500 hover:text-[#0F2D5C]"
                                onClick={() => toggleComplementOpen(it.uuid)}
                                aria-label="Detalle SAT"
                              >
                                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                            </td>
                            <td className="px-1 py-1 align-middle">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300"
                                checked={complementSel.has(it.uuid)}
                                disabled={it.conflicto_excel}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setComplementSel((prev) => {
                                    const next = new Set(prev);
                                    if (on) next.add(it.uuid);
                                    else next.delete(it.uuid);
                                    return next;
                                  });
                                  if (on) {
                                    setComplementOpts((p) => ({
                                      ...p,
                                      [it.uuid]: p[it.uuid] || {
                                        tipo: "completo",
                                        canal: "banco",
                                        importe: "",
                                        fechaCobroPago: sugerirFechaCobroPago(it),
                                        pagosParciales: [],
                                      },
                                    }));
                                  }
                                }}
                              />
                            </td>
                            <td className="px-2 py-1 font-mono whitespace-nowrap max-w-[100px] truncate" title={facturaLbl}>{facturaLbl}</td>
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{it.fecha || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(it.total || 0).toFixed(2)}</td>
                            <td className="px-1 py-1 text-center font-mono text-slate-700">{it.moneda || "MXN"}</td>
                            <td className="px-1 py-1 text-right font-mono text-slate-600">
                              {it.tipo_cambio != null && it.tipo_cambio !== "" ? Number(it.tipo_cambio).toFixed(4) : "—"}
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-medium text-[#0F2D5C]">
                              {it.cobrado_mxn_sat != null ? Number(it.cobrado_mxn_sat).toFixed(2) : "—"}
                            </td>
                            <td className="px-1 py-1 align-middle text-center">
                              <button
                                type="button"
                                disabled={!complementSel.has(it.uuid) || it.conflicto_excel}
                                title="Abrir captura: pago o cobro completo (fecha editable)"
                                aria-pressed={opt.tipo === "completo"}
                                aria-label="Pago completo, abrir ventana de captura"
                                className={`inline-flex h-5 w-5 items-center justify-center rounded border text-[10px] font-bold leading-none shrink-0 disabled:opacity-40 disabled:pointer-events-none ${opt.tipo === "completo" ? "border-[#0F2D5C] bg-[#0F2D5C] text-white" : "border-slate-300 bg-white text-slate-500 hover:bg-slate-50"}`}
                                onClick={() => openComplementModal(it.uuid, "completo")}
                              >
                                {opt.tipo === "completo" ? "✓" : ""}
                              </button>
                            </td>
                            <td className="px-1 py-1 align-middle text-center">
                              <button
                                type="button"
                                disabled={!complementSel.has(it.uuid) || it.conflicto_excel}
                                title="Abrir captura: parcialidades (fechas e importes)"
                                aria-pressed={opt.tipo === "parcial"}
                                aria-label="Parcialidades, abrir ventana de captura"
                                className={`inline-flex h-5 w-5 items-center justify-center rounded border text-[10px] font-bold leading-none shrink-0 disabled:opacity-40 disabled:pointer-events-none ${opt.tipo === "parcial" ? "border-[#0F2D5C] bg-[#0F2D5C] text-white" : "border-slate-300 bg-white text-slate-500 hover:bg-slate-50"}`}
                                onClick={() => openComplementModal(it.uuid, "parcial")}
                              >
                                {opt.tipo === "parcial" ? "✓" : ""}
                              </button>
                            </td>
                            <td className="px-2 py-1 text-slate-700 max-w-[140px]">
                              <div className="flex flex-col gap-0.5">
                                <span className="leading-tight" title={resumenTxt}>{resumenTxt}</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-6 text-[9px] px-1.5 w-fit"
                                  disabled={!complementSel.has(it.uuid) || it.conflicto_excel}
                                  onClick={() => openComplementModal(it.uuid)}
                                >
                                  Capturar…
                                </Button>
                              </div>
                            </td>
                            <td className="px-2 py-1 max-w-[120px] truncate" title={it.contraparte}>{it.contraparte || "—"}</td>
                            <td className="px-2 py-1"><span className="font-mono">{it.metodo_pago || "—"}</span></td>
                            <td className="px-2 py-1 font-mono text-slate-600 max-w-[88px] truncate" title={it.uuid}>{it.uuid}</td>
                          </tr>
                          {open && (
                            <tr className="bg-slate-50/90 border-t border-slate-100">
                              <td colSpan={14} className="px-3 py-3 text-[11px] space-y-3 align-top">
                                <div className="font-semibold text-[#0F2D5C]">Referencia SAT · {facturaLbl}</div>
                                {(it.pagos_sat || []).length > 0 && (
                                  <div className="rounded border border-slate-200 bg-white p-2 space-y-1">
                                    <div className="text-[10px] font-semibold text-slate-700">Pagos detectados en XML (CFDI tipo P)</div>
                                    <div className="overflow-x-auto max-h-28 overflow-y-auto">
                                      <table className="w-full text-[10px] font-mono">
                                        <thead>
                                          <tr className="text-left text-slate-500 border-b">
                                            <th className="py-0.5 pr-2">Fecha pago</th>
                                            <th className="py-0.5 pr-2 text-right">Importe</th>
                                            <th className="py-0.5 pr-2 text-right">Acum.</th>
                                            <th className="py-0.5">UUID pago</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(it.pagos_sat || []).map((p, idx) => (
                                            <tr key={`${p.pago_cfdi_uuid}-${idx}`} className="border-t border-slate-100">
                                              <td className="py-0.5 pr-2">{p.fecha_pago || "—"}</td>
                                              <td className="py-0.5 pr-2 text-right">{Number(p.imp_pagado || 0).toFixed(2)}</td>
                                              <td className="py-0.5 pr-2 text-right">{Number(p.acumulado || 0).toFixed(2)}</td>
                                              <td className="py-0.5 truncate max-w-[120px]" title={p.pago_cfdi_uuid}>{(p.pago_cfdi_uuid || "").slice(0, 10)}…</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="flex flex-wrap gap-3 text-[10px] text-slate-600 items-center">
                                      <span>Suma pagos SAT: <strong className="text-slate-900">{Number(it.total_pagado_sat || 0).toFixed(2)}</strong></span>
                                      <span>Saldo vs pagos SAT: <strong className="text-slate-900">{Number(it.saldo_restante_cfdi || 0).toFixed(2)}</strong></span>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        className="h-7 text-[10px] px-2"
                                        disabled={!complementSel.has(it.uuid) || it.conflicto_excel}
                                        onClick={() => {
                                          const pagos = it.pagos_sat || [];
                                          const lastFp = pagos.length ? String(pagos[pagos.length - 1].fecha_pago || "").slice(0, 10) : "";
                                          const emi = String(it.fecha || "").slice(0, 10);
                                          const impSat = String(Number(it.total_pagado_sat || 0).toFixed(2));
                                          setComplementOpts((p) => ({
                                            ...p,
                                            [it.uuid]: {
                                              tipo: "parcial",
                                              canal: "banco",
                                              importe: impSat,
                                              fechaCobroPago: lastFp || sugerida,
                                              pagosParciales: [{ fecha: lastFp || emi, importe: impSat, canal: "banco" }],
                                            },
                                          }));
                                          toast.success("Se cargó un abono con la suma SAT. Revisa en «Capturar…» o guarda.");
                                        }}
                                      >
                                        Usar suma pagos SAT (1 abono)
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {!(it.pagos_sat || []).length && (
                                  <p className="text-[10px] text-slate-500">
                                    Sin complementos de pago en BD para este UUID. Sugerencia de fecha al guardar si no indicas otra:{" "}
                                    <span className="font-mono">{sugerida || "—"}</span>
                                  </p>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Dialog
                open={Boolean(complementModalUuid)}
                onOpenChange={(v) => {
                  if (!v) {
                    setComplementModalUuid(null);
                    setComplementModalDraft(null);
                  }
                }}
              >
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-[#0F2D5C]">
                      {complementFlujo === "ingreso" ? "Cobro / control" : "Pago / control"}
                    </DialogTitle>
                  </DialogHeader>
                  {complementModalItem && complementModalDraft && (
                    <div className="space-y-4 text-sm">
                      <div className="text-[11px] text-slate-600 space-y-0.5">
                        <p>
                          <span className="font-semibold">Factura:</span>{" "}
                          {[complementModalItem.serie, complementModalItem.folio].filter(Boolean).join("-") || "—"}
                        </p>
                        <p>
                          <span className="font-semibold">Total CFDI:</span>{" "}
                          <span className="font-mono">{Number(complementModalItem.total || 0).toFixed(2)}</span>
                          {" · "}
                          <span className="font-semibold">Emisión:</span>{" "}
                          <span className="font-mono">{complementModalItem.fecha || "—"}</span>
                        </p>
                        <p className="font-mono text-[10px] text-slate-500 truncate" title={complementModalItem.uuid}>{complementModalItem.uuid}</p>
                      </div>

                      <div className="flex flex-col gap-3 border border-slate-200 rounded-md p-3 bg-slate-50/80">
                        <fieldset className="space-y-3 border-0 p-0 m-0">
                          <legend className="sr-only">Tipo de cobro o pago</legend>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="complementModalTipo"
                              className="mt-1"
                              checked={complementModalDraft.tipo === "completo"}
                              onChange={() => {
                                const emi = String(complementModalItem.fecha || "").slice(0, 10);
                                setComplementModalDraft((d) =>
                                  d
                                    ? {
                                        ...d,
                                        tipo: "completo",
                                        fechaCobroPago: d.fechaCobroPago && String(d.fechaCobroPago).trim() ? d.fechaCobroPago : emi,
                                        pagosParciales: [],
                                        canal: d.canal === "efectivo" ? "efectivo" : "banco",
                                      }
                                    : d,
                                );
                              }}
                            />
                            <span>
                              <span className="font-medium">Pago o cobro completo</span>
                              <span className="block text-[11px] text-slate-600">Se considera el total del CFDI. La fecha de control parte de la emisión y puedes corregirla.</span>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="complementModalTipo"
                              className="mt-1"
                              checked={complementModalDraft.tipo === "parcial"}
                              onChange={() => {
                                const emi = String(complementModalItem.fecha || "").slice(0, 10);
                                setComplementModalDraft((d) => {
                                  if (!d) return d;
                                  const nextPagos =
                                    d.pagosParciales && d.pagosParciales.length
                                      ? d.pagosParciales.map((line) => ({
                                          fecha: line.fecha,
                                          importe: line.importe,
                                          canal: line.canal === "efectivo" ? "efectivo" : "banco",
                                        }))
                                      : [{ fecha: emi, importe: "", canal: "banco" }];
                                  return { ...d, tipo: "parcial", pagosParciales: nextPagos, fechaCobroPago: "" };
                                });
                              }}
                            />
                            <span>
                              <span className="font-medium">Pago o cobro en parcialidades</span>
                              <span className="block text-[11px] text-slate-600">Agrega una fila por abono (fecha e importe). El saldo se calcula respecto al total del CFDI.</span>
                            </span>
                          </label>
                        </fieldset>
                      </div>

                      {complementModalDraft.tipo === "completo" && (
                        <div className="space-y-2">
                          <Label className="text-xs">Fecha de cobro o pago (control)</Label>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              type="date"
                              className="h-9 text-xs font-mono max-w-[180px]"
                              value={String(complementModalDraft.fechaCobroPago || "").slice(0, 10)}
                              onChange={(e) =>
                                setComplementModalDraft((d) => (d ? { ...d, fechaCobroPago: e.target.value } : d))
                              }
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="h-9 text-xs"
                              onClick={() => {
                                const emi = String(complementModalItem.fecha || "").slice(0, 10);
                                setComplementModalDraft((d) => (d ? { ...d, fechaCobroPago: emi } : d));
                              }}
                            >
                              Usar emisión
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-9 text-xs text-slate-600"
                              onClick={() => setComplementModalDraft((d) => (d ? { ...d, fechaCobroPago: "" } : d))}
                            >
                              Limpiar
                            </Button>
                          </div>
                          <p className="text-[11px] text-slate-500">Si dejas la fecha vacía, al guardar el sistema usará la sugerencia (último pago SAT o emisión).</p>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Canal (banco vs efectivo)</Label>
                            <Select
                              value={complementModalDraft.canal === "efectivo" ? "efectivo" : "banco"}
                              onValueChange={(v) =>
                                setComplementModalDraft((d) => (d ? { ...d, canal: v } : d))
                              }
                            >
                              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="banco">Banco / transferencia</SelectItem>
                                <SelectItem value="efectivo">Efectivo</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-[10px] text-slate-500">Este dato alimenta el cuadre del mes por fecha de cobro o pago.</p>
                          </div>
                        </div>
                      )}

                      {complementModalDraft.tipo === "parcial" && (
                        <div className="space-y-3">
                          <div className="flex flex-wrap justify-between gap-2 text-[11px]">
                            <span>
                              Suma abonos:{" "}
                              <strong className="font-mono">
                                {complementModalDraft.pagosParciales
                                  .reduce((a, p) => a + (parseFloat(String(p.importe || "").replace(/,/g, "")) || 0), 0)
                                  .toFixed(2)}
                              </strong>
                            </span>
                            <span>
                              Saldo:{" "}
                              <strong className="font-mono">
                                {Math.max(
                                  (Number(complementModalItem.total) || 0) -
                                    complementModalDraft.pagosParciales.reduce(
                                      (a, p) => a + (parseFloat(String(p.importe || "").replace(/,/g, "")) || 0),
                                      0,
                                    ),
                                  0,
                                ).toFixed(2)}
                              </strong>
                            </span>
                          </div>
                          <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-[11px]">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Fecha abono</th>
                                  <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Importe</th>
                                  <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Canal</th>
                                  <th className="w-16 px-1 py-1.5" />
                                </tr>
                              </thead>
                              <tbody>
                                {complementModalDraft.pagosParciales.map((line, idx) => (
                                  <tr key={idx} className="border-t border-slate-100">
                                    <td className="px-2 py-1 align-middle">
                                      <Input
                                        type="date"
                                        className="h-8 text-[11px] font-mono"
                                        value={String(line.fecha || "").slice(0, 10)}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setComplementModalDraft((d) => {
                                            if (!d) return d;
                                            const next = [...d.pagosParciales];
                                            next[idx] = { ...next[idx], fecha: v };
                                            return { ...d, pagosParciales: next };
                                          });
                                        }}
                                      />
                                    </td>
                                    <td className="px-2 py-1 align-middle">
                                      <Input
                                        className="h-8 text-[11px] font-mono text-right"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        value={line.importe}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setComplementModalDraft((d) => {
                                            if (!d) return d;
                                            const next = [...d.pagosParciales];
                                            next[idx] = { ...next[idx], importe: v };
                                            return { ...d, pagosParciales: next };
                                          });
                                        }}
                                      />
                                    </td>
                                    <td className="px-2 py-1 align-middle">
                                      <Select
                                        value={line.canal === "efectivo" ? "efectivo" : "banco"}
                                        onValueChange={(v) =>
                                          setComplementModalDraft((d) => {
                                            if (!d) return d;
                                            const next = [...d.pagosParciales];
                                            next[idx] = { ...next[idx], canal: v };
                                            return { ...d, pagosParciales: next };
                                          })
                                        }
                                      >
                                        <SelectTrigger className="h-8 text-[10px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="banco">Banco</SelectItem>
                                          <SelectItem value="efectivo">Efectivo</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-1 py-1 align-middle text-center">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-1 text-destructive"
                                        disabled={complementModalDraft.pagosParciales.length <= 1}
                                        onClick={() =>
                                          setComplementModalDraft((d) => {
                                            if (!d || d.pagosParciales.length <= 1) return d;
                                            const next = d.pagosParciales.filter((_, j) => j !== idx);
                                            return { ...d, pagosParciales: next };
                                          })
                                        }
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() =>
                              setComplementModalDraft((d) => {
                                if (!d) return d;
                                const emi = String(complementModalItem?.fecha || "").slice(0, 10);
                                return { ...d, pagosParciales: [...d.pagosParciales, { fecha: emi, importe: "", canal: "banco" }] };
                              })
                            }
                          >
                            Agregar otro abono
                          </Button>
                        </div>
                      )}

                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => { setComplementModalUuid(null); setComplementModalDraft(null); }}>
                          Cancelar
                        </Button>
                        <Button type="button" className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white" onClick={applyComplementModal}>
                          Aplicar
                        </Button>
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              {complementItems.some((i) => i.conflicto_excel) && (
                <p className="text-[11px] text-amber-900 flex items-start gap-1">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Las filas en fondo amarillo ya tienen ese UUID en datos cargados por Excel/CSV. No se duplican desde la conciliación manual; usa solo una fuente por UUID.
                </p>
              )}
              <Button type="button" onClick={saveModoComplemento} disabled={complementSaveBusy || !clientId} className="h-9 bg-[#0F2D5C] hover:bg-[#0b2249] text-white text-xs">
                {complementSaveBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Guardar control ({complementSel.size} facturas)
              </Button>
            </div>

              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="excel" className="border-slate-200 px-0 border-b-0">
            <AccordionTrigger data-testid="tab-conciliacion-excel">
              <span className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Conciliación manual en Excel
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
              <div className="pt-4">
                <ConciliacionManualExcelPanel
                  clientId={clientId}
                  year={year}
                  onImported={() => {
                    loadDiagnostics();
                    loadModoComplemento(false);
                  }}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
  );

  if (isEmbedded) {
    return accordionBlock;
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        title={CONCILIACION_MANUAL_CFDI_TITLE}
        subtitle="Control de cobros y pagos desde CFDI cargados (SAT). Usa la captura y el cuadre mensual por fecha de cobro o pago."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, CONCILIACION_MANUAL_CFDI_TITLE]}
      />

      <div className="p-8 space-y-5">
        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="conciliacion-client-select" className="mt-1 h-auto min-h-[40px] py-2 text-left">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent className="max-w-[600px]">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="py-2">
                      <div className="flex flex-col items-start text-left">
                        <div className="font-bold text-sm whitespace-normal leading-tight">{c.razon_social}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">
                          {c.rfc} {c.owner_email ? ` · ${c.owner_email}` : ""}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Ejercicio (conciliación manual)</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-end gap-3 text-xs">
            <div className="flex-1 bg-slate-50 rounded-sm p-2.5 border border-slate-200">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">CFDIs cargados ({year})</div>
              <div className="text-lg font-bold text-[#0F2D5C]">{totalCfdi}</div>
            </div>
            <div className="flex-1 bg-slate-50 rounded-sm p-2.5 border border-slate-200">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Filas Excel/CSV ({year})</div>
              <div className="text-lg font-bold text-[#0F2D5C]">{excelStats?.count || 0}</div>
            </div>
          </div>
        </div>

        {accordionBlock}
      </div>
    </div>
  );
}
